import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, ArrowsClockwise, Question } from "@phosphor-icons/react";

/**
 * ResendDomainHealthCard — admin-only banner showing the live status of
 * our Resend sending domain. Driven by an hourly cron on the backend
 * that polls Resend's /domains API and alerts every admin via email
 * the first time the status transitions (verified → not_verified, etc).
 *
 * Real bug report 2026-05: the 6AM Admin Daily Digest failed silently
 * for 3 days before being noticed because we had no in-app surface for
 * Resend domain health. This card closes that gap — green when fine,
 * red when broken, with a one-click "Re-check now" that fires the
 * health-check on demand.
 */
const STATUS_META = {
  verified: {
    label: "Verified",
    tone: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
    Icon: CheckCircle,
  },
  pending: {
    label: "Pending",
    tone: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
    Icon: WarningCircle,
  },
  not_verified: {
    label: "Not verified",
    tone: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    Icon: WarningCircle,
  },
  failed: {
    label: "Failed",
    tone: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    Icon: WarningCircle,
  },
  not_in_account: {
    label: "Not on Resend account",
    tone: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    Icon: WarningCircle,
  },
  insufficient_scope: {
    // The configured RESEND_API_KEY is a "Sending-only" key, so we
    // can't read /domains. Emails still deliver fine — this is purely
    // an in-app health-check limitation. Render as informational, not
    // an alarm, so admins don't think delivery is broken.
    label: "Health check disabled (Sending-only API key)",
    tone: "bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-900/60 dark:text-zinc-300 dark:border-zinc-700",
    Icon: Question,
    informational: true,
  },
  disabled: {
    label: "Health check disabled",
    tone: "bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-900/60 dark:text-zinc-300 dark:border-zinc-700",
    Icon: Question,
    informational: true,
  },
  api_unreachable: {
    // Transient — Resend's /domains endpoint refused the call for
    // some reason other than auth (rate limit, 5xx, network blip).
    // Surface as informational, not an alarm; the hourly cron will
    // self-heal as soon as the next call succeeds.
    label: "Health check temporarily unavailable",
    tone: "bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-900/60 dark:text-zinc-300 dark:border-zinc-700",
    Icon: Question,
    informational: true,
  },
};

const FALLBACK_META = {
  label: "Unknown",
  tone: "bg-zinc-100 text-zinc-800 border-zinc-300 dark:bg-zinc-900/60 dark:text-zinc-300 dark:border-zinc-700",
  Icon: Question,
};

function relativeTime(iso) {
  if (!iso) return "never";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } catch {
    return "unknown";
  }
}

export default function ResendDomainHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/resend-domain-status");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load Resend domain status"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recheck = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post("/admin/cron/resend-domain-health");
      setData(d?.stats?.checked ? { available: true, ...d.stats } : data);
      const newStatus = d?.stats?.status;
      if (newStatus === "verified") {
        toast.success("Domain re-checked · verified.");
      } else if (
        newStatus === "insufficient_scope" ||
        newStatus === "disabled" ||
        newStatus === "api_unreachable"
      ) {
        toast.info("Health check unavailable — emails still deliver normally.");
      } else if (newStatus) {
        toast.warning(`Domain status: ${newStatus}`);
      } else {
        toast.error("Re-check failed — see backend logs.");
      }
      // Reload from the persisted state to get the freshest data.
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Re-check failed"));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !data) {
    return (
      <section className="mb-4" data-testid="resend-domain-health-card">
        <div className="h-14 rounded-xl bg-muted/60 animate-pulse" />
      </section>
    );
  }
  if (!data?.available) {
    return null; // nothing to show until first check has run
  }

  const status = (data.status || "").toLowerCase();
  const meta = STATUS_META[status] || FALLBACK_META;
  const Icon = meta.Icon;
  const informational = !!meta.informational;
  const isBroken = !informational && status && status !== "verified" && status !== "pending";

  return (
    <section
      className={`mb-4 rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap ${meta.tone}`}
      data-testid="resend-domain-health-card"
      role={isBroken ? "alert" : undefined}
    >
      <Icon size={20} weight="duotone" className="flex-none" />
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-sm font-bold">
          Email sending domain:{" "}
          <span className="font-semibold">{data.domain || "(unknown)"}</span>{" "}
          · {meta.label}
        </div>
        <div className="text-[11px] opacity-80 mt-0.5">
          Last checked {relativeTime(data.last_checked_at)}
          {data.last_status_change_at &&
            data.last_status_change_at !== data.last_checked_at && (
              <>
                {" · "}status last changed {relativeTime(data.last_status_change_at)}
              </>
            )}
          {informational && data.detail && (
            <div className="mt-1 opacity-90">{data.detail}</div>
          )}
        </div>
      </div>
      {isBroken && (
        <a
          href="https://resend.com/domains"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-none inline-flex items-center px-3 h-8 rounded-full bg-foreground text-background text-xs font-bold hover:opacity-90"
          data-testid="resend-domain-fix-link"
        >
          Fix on Resend
        </a>
      )}
      <button
        type="button"
        onClick={recheck}
        disabled={refreshing}
        className="flex-none inline-flex items-center gap-1 px-3 h-8 rounded-full border border-current text-xs font-bold hover:opacity-90 disabled:opacity-50"
        data-testid="resend-domain-recheck"
      >
        <ArrowsClockwise
          size={12}
          weight="bold"
          className={refreshing ? "animate-spin" : ""}
        />
        Re-check
      </button>
    </section>
  );
}
